import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createRangeSelection,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_HIGH,
  KEY_ARROW_LEFT_COMMAND,
  KEY_ARROW_RIGHT_COMMAND,
} from 'lexical';
import { useEffect } from 'react';

import { SlotNode } from '../SlotNode';

export const SlotNavigationPlugin = (): null => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const unregisterLeft = editor.registerCommand(
      KEY_ARROW_LEFT_COMMAND,
      (event) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
          return false;
        }

        const anchorNode = selection.anchor.getNode();
        const anchorOffset = selection.anchor.offset;

        if (anchorOffset === 0) {
          const prevSibling = anchorNode.getPreviousSibling();
          if (prevSibling instanceof SlotNode) {
            event?.preventDefault();
            const prevPrevSibling = prevSibling.getPreviousSibling();
            if (prevPrevSibling) {
              const newSelection = $createRangeSelection();
              if (prevPrevSibling.getType() === 'text') {
                newSelection.anchor.set(
                  prevPrevSibling.getKey(),
                  prevPrevSibling.getTextContentSize(),
                  'text',
                );
                newSelection.focus.set(
                  prevPrevSibling.getKey(),
                  prevPrevSibling.getTextContentSize(),
                  'text',
                );
              } else {
                newSelection.anchor.set(prevPrevSibling.getKey(), 0, 'element');
                newSelection.focus.set(prevPrevSibling.getKey(), 0, 'element');
              }
              $setSelection(newSelection);
            } else {
              const parent = prevSibling.getParent();
              if (parent) {
                const newSelection = $createRangeSelection();
                newSelection.anchor.set(parent.getKey(), 0, 'element');
                newSelection.focus.set(parent.getKey(), 0, 'element');
                $setSelection(newSelection);
              }
            }
            return true;
          }
        }

        if (anchorNode instanceof SlotNode) {
          event?.preventDefault();
          const prevSibling = anchorNode.getPreviousSibling();
          if (prevSibling) {
            const newSelection = $createRangeSelection();
            if (prevSibling.getType() === 'text') {
              newSelection.anchor.set(
                prevSibling.getKey(),
                prevSibling.getTextContentSize(),
                'text',
              );
              newSelection.focus.set(
                prevSibling.getKey(),
                prevSibling.getTextContentSize(),
                'text',
              );
            } else {
              newSelection.anchor.set(prevSibling.getKey(), 0, 'element');
              newSelection.focus.set(prevSibling.getKey(), 0, 'element');
            }
            $setSelection(newSelection);
          }
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    const unregisterRight = editor.registerCommand(
      KEY_ARROW_RIGHT_COMMAND,
      (event) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
          return false;
        }

        const anchorNode = selection.anchor.getNode();
        const anchorOffset = selection.anchor.offset;

        const isAtEnd =
          anchorNode.getType() === 'text' &&
          anchorOffset === anchorNode.getTextContentSize();
        if (isAtEnd) {
          const nextSibling = anchorNode.getNextSibling();
          if (nextSibling instanceof SlotNode) {
            event?.preventDefault();
            const nextNextSibling = nextSibling.getNextSibling();
            if (nextNextSibling) {
              const newSelection = $createRangeSelection();
              if (nextNextSibling.getType() === 'text') {
                newSelection.anchor.set(nextNextSibling.getKey(), 0, 'text');
                newSelection.focus.set(nextNextSibling.getKey(), 0, 'text');
              } else {
                newSelection.anchor.set(nextNextSibling.getKey(), 0, 'element');
                newSelection.focus.set(nextNextSibling.getKey(), 0, 'element');
              }
              $setSelection(newSelection);
            } else {
              const parent = nextSibling.getParent();
              if (parent) {
                const newSelection = $createRangeSelection();
                const childCount = parent.getChildrenSize();
                newSelection.anchor.set(parent.getKey(), childCount, 'element');
                newSelection.focus.set(parent.getKey(), childCount, 'element');
                $setSelection(newSelection);
              }
            }
            return true;
          }
        }

        if (anchorNode instanceof SlotNode) {
          event?.preventDefault();
          const nextSibling = anchorNode.getNextSibling();
          if (nextSibling) {
            const newSelection = $createRangeSelection();
            if (nextSibling.getType() === 'text') {
              newSelection.anchor.set(nextSibling.getKey(), 0, 'text');
              newSelection.focus.set(nextSibling.getKey(), 0, 'text');
            } else {
              newSelection.anchor.set(nextSibling.getKey(), 0, 'element');
              newSelection.focus.set(nextSibling.getKey(), 0, 'element');
            }
            $setSelection(newSelection);
          }
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    return () => {
      unregisterLeft();
      unregisterRight();
    };
  }, [editor]);

  return null;
};
